/**
 * Table search value + wire-payload helpers, shared by the filter panel and the
 * three surfaces that forward it into `widget_config`. Pure (no React) so the
 * payload rules are unit-testable.
 *
 * Two filter modes, distinguished by the selected column's `type`:
 *  - **dimension** (or "all columns"): free-text `search_text`, optionally
 *    scoped to one `search_column`.
 *  - **measure**: a numeric comparison — `search_column` + `search_operator` +
 *    `search_value` (+ `search_value_2` for `BETWEEN`).
 */

export type SearchOperator = '=' | '<' | '<=' | '>' | '>=' | 'BETWEEN';

export const SEARCH_OPERATORS: SearchOperator[] = [
  '=',
  '<',
  '<=',
  '>',
  '>=',
  'BETWEEN',
];

export type TableSearch = {
  /** Column to scope to. Required for measure; optional for text (omit ⇒ all). */
  search_column?: string;
  /** Dimension free-text query. */
  search_text?: string;
  /** Measure comparison operator. */
  search_operator?: SearchOperator;
  /** Measure comparison value (null while the input is empty/invalid). */
  search_value?: number | null;
  /** Second measure value — only for the `BETWEEN` operator. */
  search_value_2?: number | null;
};

export const EMPTY_TABLE_SEARCH: TableSearch = Object.freeze({});

/**
 * A measure search is only usable once it has a column, an operator, and a
 * numeric value — and, for `BETWEEN`, a second value.
 */
export function isMeasureSearchComplete(s: TableSearch): boolean {
  if (!s.search_column || !s.search_operator || s.search_value == null) {
    return false;
  }
  if (s.search_operator === 'BETWEEN' && s.search_value_2 == null) {
    return false;
  }
  return true;
}

/** Whether the search currently filters anything (drives the button's active state). */
export function isTableSearchActive(s: TableSearch): boolean {
  if (isMeasureSearchComplete(s)) {
    return true;
  }
  return (s.search_text?.trim().length ?? 0) > 0;
}

/**
 * The `widget_config` fragment for a search — a measure comparison, a text
 * search, or `{}` when nothing is active. Measure wins when complete; the two
 * modes are mutually exclusive by construction (the panel sets one or the other).
 */
export function buildTableSearchConfig(
  s: TableSearch,
): Record<string, unknown> {
  if (isMeasureSearchComplete(s)) {
    const config: Record<string, unknown> = {
      search_column: s.search_column,
      search_operator: s.search_operator,
      search_value: s.search_value,
    };
    if (s.search_operator === 'BETWEEN') {
      config.search_value_2 = s.search_value_2;
    }
    return config;
  }
  const text = s.search_text?.trim();
  if (text) {
    return s.search_column
      ? {search_column: s.search_column, search_text: text}
      : {search_text: text};
  }
  return {};
}
